export const RENT_STEPS = [
  { code: "warehouse", number: "01", label: "Выберите склад" },
  { code: "box", number: "02", label: "Размер бокса" },
  { code: "personal", number: "03", label: "Личные данные" },
  { code: "payment", number: "04", label: "Оплата" },
] as const

export type RentStepCode = (typeof RENT_STEPS)[number]["code"]
export type PersonalPhase = "auth" | "customer"

function getWeekLabel(weeks: number): string {
  if (weeks === 1) {
    return "1 неделя"
  }

  return `${weeks} недели`
}

function getMonthLabel(months: number): string {
  if (months === 1) {
    return "1 месяц"
  }

  if (months >= 2 && months <= 4) {
    return `${months} месяца`
  }

  return `${months} месяцев`
}

export const RENT_PERIOD_OPTIONS = [
  ...([1, 2, 3, 4] as const).map((weeks) => ({
    value: `${weeks}w` as const,
    label: getWeekLabel(weeks),
  })),
  ...([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] as const).map((months) => ({
    value: `${months}m` as const,
    label: getMonthLabel(months),
  })),
] as const

export type RentPeriod = (typeof RENT_PERIOD_OPTIONS)[number]["value"]

export const RENT_DEFAULT_PERIOD: RentPeriod = "1m"

export type RentPaymentPlanOption = {
  value: RentPeriod
  badge: string
  price: number
  priceBefore?: number
  mainPriceLabel: string
  totalLabel: string
  discountBadge?: string
}

const RENT_PAYMENT_PLAN_META: Array<{
  value: RentPeriod
  badge: string
  unit: "w" | "m"
  count: number
}> = [
  { value: "1w", badge: "по неделям", unit: "w", count: 1 },
  { value: "1m", badge: "по месяцам", unit: "m", count: 1 },
  { value: "6m", badge: "6 месяцев", unit: "m", count: 6 },
  { value: "11m", badge: "11 месяцев", unit: "m", count: 11 },
]

function formatRub(amount: number): string {
  return `${new Intl.NumberFormat("ru-RU").format(Math.round(amount))} ₽`
}

function formatSubPeriodPrice(total: number, count: number, unit: "w" | "m"): string {
  const perUnit = Math.round(total / count)
  return unit === "w" ? `${formatRub(perUnit)}/нед` : `${formatRub(perUnit)}/мес`
}

function formatDiscountBadge(total: number, totalBefore: number): string | undefined {
  if (totalBefore <= total) {
    return undefined
  }

  const percent = Math.round((1 - total / totalBefore) * 100)
  if (percent <= 0) {
    return undefined
  }

  return `-${percent}%`
}

export function buildRentPaymentPlanOptions(
  items: Array<{ period: string; total: number; totalBefore: number }>,
): RentPaymentPlanOption[] {
  return RENT_PAYMENT_PLAN_META.flatMap((meta) => {
    const priceItem = items.find((item) => item.period === meta.value)
    if (!priceItem) {
      return []
    }

    const { total, totalBefore } = priceItem
    const discountBadge = formatDiscountBadge(total, totalBefore)

    return [
      {
        value: meta.value,
        badge: meta.badge,
        price: total,
        priceBefore: totalBefore > total ? totalBefore : undefined,
        mainPriceLabel: formatSubPeriodPrice(total, meta.count, meta.unit),
        totalLabel: formatRub(total),
        discountBadge,
      },
    ]
  })
}

export function getRentPaymentPlanOption(
  period: RentPeriod | "",
  options: RentPaymentPlanOption[],
): RentPaymentPlanOption | undefined {
  if (!period) {
    return undefined
  }

  return options.find((option) => option.value === period)
}

export function getTodayDateString(): string {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, "0")
  const day = String(today.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export function parseRentDateString(value: string): Date {
  const [year, month, day] = value.split("-").map(Number)
  return new Date(year, month - 1, day)
}

export function toRentDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

function parseDateString(value: string): Date {
  return parseRentDateString(value)
}

function toDateString(date: Date): string {
  return toRentDateString(date)
}

export function getTodayRentDate(): Date {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today
}

export function getMaxRentDate(): Date {
  const maxDate = getTodayRentDate()
  maxDate.setDate(maxDate.getDate() + 7)
  return maxDate
}

export function getNextRentDates(count = 8): string[] {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return Array.from({ length: count }, (_, index) => {
    const date = new Date(today)
    date.setDate(today.getDate() + index)
    return toDateString(date)
  })
}

export function getMaxRentDateString(): string {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  today.setDate(today.getDate() + 7)
  return toDateString(today)
}

export function formatRentWeekdayShort(value: string): string {
  const date = parseDateString(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat("ru-RU", { weekday: "short" })
    .format(date)
    .replace(".", "")
    .toLowerCase()
}

export function formatRentWeekdayLabel(value: string): string {
  if (value === toDateString(getTodayRentDate())) {
    return "сегодня"
  }

  return formatRentWeekdayShort(value).toUpperCase()
}

export function formatRentDateShort(value: string): string {
  const date = parseDateString(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }

  const formatted = new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "short",
  }).format(date)

  return formatted.endsWith(".") ? formatted : `${formatted}.`
}

export function formatRentMoveInDate(value: string): string {
  const date = parseDateString(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }

  const formatted = new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
  }).format(date)

  return `с ${formatted}`
}
